import {
  RESOURCE_BINDINGS,
  type ResourceBindingContext,
  type ResourceBindingDefinition,
} from '@seen/core/resourceBindingRegistry'
import { createElement } from 'react'
import type { ComboboxOption } from '#src/components/core/combobox/util.ts'
import {
  hasBindingContext,
  registryVariableContextLabel,
  resolveBindingValue,
} from '#src/components/rich-text-editor/registry-variable/registryVariableResolution.ts'
import { RegistryVariableOptionLabel } from '#src/components/rich-text-editor/registry-variable/RegistryVariableView.tsx'

export const REGISTRY_VARIABLE_OPTION_PREFIX = 'registry-variable/'
const UNAVAILABLE_REGISTRY_VARIABLE_OPTION_PREFIX =
  'registry-variable-unavailable/'

// Sensitive bindings (SSN) never go into note text, and reference-only
// bindings have no text to show.
function isInsertable(binding: ResourceBindingDefinition): boolean {
  return (
    !binding.sensitive &&
    binding.answerTypes.some((answerType) => answerType !== 'reference')
  )
}

export function isRegistryVariableOption(value: string): boolean {
  return (
    value.startsWith(REGISTRY_VARIABLE_OPTION_PREFIX) ||
    value.startsWith(UNAVAILABLE_REGISTRY_VARIABLE_OPTION_PREFIX)
  )
}

export function registryVariablePickerOptions({
  isTemplate,
  contexts,
}: {
  isTemplate: boolean
  contexts: ResourceBindingContext | undefined
}): ComboboxOption<string>[] {
  return RESOURCE_BINDINGS.filter(isInsertable).map((binding) => {
    const available = isTemplate || hasBindingContext(binding, contexts ?? {})
    const contextLabel = registryVariableContextLabel(binding.context)
    return {
      value: `${available ? REGISTRY_VARIABLE_OPTION_PREFIX : UNAVAILABLE_REGISTRY_VARIABLE_OPTION_PREFIX}${binding.id}`,
      disabled: !available,
      label: createElement(RegistryVariableOptionLabel, { binding, available }),
      keywords: [
        'variable',
        binding.label,
        binding.id,
        contextLabel,
        ...('token' in binding && binding.token ? [binding.token] : []),
      ],
    }
  })
}

export interface RegistryVariableContent {
  type: 'registryVariable'
  attrs: { id: string; value: string | null; resolvedAt: string | null }
}

// The node a picked variable inserts: deferred in a template, resolved now
// everywhere else (the option is only enabled when its context is present).
export function registryVariableContent(
  id: string,
  {
    isTemplate,
    contexts,
  }: { isTemplate: boolean; contexts: ResourceBindingContext | undefined }
): RegistryVariableContent {
  const value = isTemplate ? null : resolveBindingValue(id, contexts ?? {})
  return {
    type: 'registryVariable',
    attrs: {
      id,
      value,
      resolvedAt: value === null ? null : new Date().toISOString(),
    },
  }
}
